import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe } from '../queries/auth';
import { USER_ROLES } from '../constants';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: userData, refetch, isRefetching, isLoading } = useQuery(getMe({}));

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      localStorage.removeItem('authToken');
      setUser(null);
      setLoading(false);
      return;
    }
    await refetch();
  }, [refetch]);

  const login = useCallback(
    async (userData) => {
      setUser(userData);
      await refetch();

      // Redirect based on role
      if (userData.role === USER_ROLES.CLIENT) {
        navigate('/programs');
      } else if (userData.role === USER_ROLES.LEAD_AGENT || userData.role === USER_ROLES.ADMIN) {
        navigate('/opportunities');
      } else {
        navigate('/dashboard'); // fallback
      }
    },
    [navigate, refetch],
  );

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    queryClient.clear();
    setUser(null);
    navigate('/');
  }, [navigate, queryClient]);

  useEffect(() => {
    if (!isRefetching && !isLoading) {
      if (userData?.email) {
        setUser(userData);
      } else {
        localStorage.removeItem('authToken');
        setUser(null);
      }
      setLoading(false);
    }
  }, [isRefetching, isLoading, userData, setUser, setLoading]);

  return (
    <UserContext.Provider value={{ user, login, logout, fetchUser, loading }}>
      {!loading && children}
    </UserContext.Provider>
  );
};
